import React, { FC, useState } from 'react'

import Modal from 'uiKit/Modal'
import { USAGE_METRIC } from 'constants/usageLimits'
import {
  SUPERVISION_MODE_LABEL,
  SUPERVISION_MODE_OPTIONS,
} from 'constants/supervisionMode'
import { requestLimitIncrease } from 'tabs/home/api/usageLimits'

import * as S from './UsageMeter.style'

interface Props {
  open: boolean
  onClose: () => void
  botId: string | number
  botName?: string
  requestedBy?: string
  /** Current mode, so the copy and the pre-selected segment both reflect it. */
  mode: string
}

/**
 * The Default/Dynamic sibling of {@link RequestLimitIncreaseModal} — same
 * shape (what it is today, what to change it to, an optional note, send),
 * because switching supervision scheduling is just as much a manual decision
 * as raising a limit. Only the "what to change it to" field differs: a
 * dropdown of the two modes instead of a number input.
 *
 * Send still goes through `requestLimitIncrease` with `requestedSupervisionMode`
 * — that is this repo's existing backend contract.
 */
export const RequestSupervisionModeModal: FC<Props> = ({ open, onClose, botId, botName, requestedBy, mode }) => {
  const otherMode = SUPERVISION_MODE_OPTIONS.find(option => option !== mode) || mode

  const [requestedMode, setRequestedMode] = useState(otherMode)
  const [reason, setReason] = useState('')
  const [sending, setSending] = useState(false)

  const handleSend = () => {
    setSending(true)
    requestLimitIncrease(botId, {
      metric: USAGE_METRIC.SUPERVISION_SESSIONS,
      requestedSupervisionMode: requestedMode,
      reason: reason.trim() || undefined,
      botName,
      requestedBy,
    })
      .then(() => onClose())
      .finally(() => setSending(false))
  }

  return (
    <Modal open={open} onClose={onClose} tabIndex="-1">
      <S.ModalBody>
        <S.ModalTitle>Request a change to supervision scheduling</S.ModalTitle>
        <S.ModalText>
          This organisation is currently scheduled on {SUPERVISION_MODE_LABEL[mode]}. Tell us what you need and
          your admin will confirm the change with you.
        </S.ModalText>

        <S.FieldLabel>
          New supervision mode
          <S.ModalModeSelect value={requestedMode} onChange={event => setRequestedMode(event.target.value)}>
            {SUPERVISION_MODE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {SUPERVISION_MODE_LABEL[option]}
              </option>
            ))}
          </S.ModalModeSelect>
        </S.FieldLabel>

        <S.FieldLabel>
          Anything we should know? (optional)
          <S.TextArea
            value={reason}
            onChange={event => setReason(event.target.value)}
          />
        </S.FieldLabel>

        <S.ModalActions>
          <S.GhostButton onClick={onClose}>Cancel</S.GhostButton>
          <S.PrimaryButton disabled={sending || requestedMode === mode} onClick={handleSend}>
            {sending ? 'Sending…' : 'Send request'}
          </S.PrimaryButton>
        </S.ModalActions>
      </S.ModalBody>
    </Modal>
  )
}

export default RequestSupervisionModeModal
